import { useState, useRef, memo, useEffect, useCallback } from "react";
import { DropdownProps } from "semantic-ui-react";
import { useIntl } from "react-intl";

// Styles
import "./MeHeyLanding.scss";

// Components
import MeHeyLandingHeader from "./MeHeyLandingHeader";
import MeHeyLandingHome from "./MeHeyLandingHome";
import MeHeyLandingFeature from "./MeHeyLandingFeature";
import MeHeyLandingAddLineMod from "./MeHeyLandingAddLineMod";
import MeHeyLandingDownloadMod from "./MeHeyLandingDownloadMod";
import MeHeyLandingLoginMod from "./MeHeyLandingLoginMod";
import LoadingScreen from "./LoadingScreen";
import MeHeyLandingFooter from "./MeHeyLandingFooter";
import MyIshealthCloudLanding from "./MyIshealthCloudLanding";

type MeHeyLandingProps = {
  AppInitializeSequence: any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  controller?: any;
  facebookLogin?: (name: string) => void;
  onLoginSubmit: () => void;
  onLoginSuccess: (res: any) => void;
  loadingLogin?: boolean;

  // language
  selectedLanguage: Language;
  onChangeSelectedLanguage?: (
    e: React.SyntheticEvent<HTMLElement> | null,
    data: DropdownProps,
  ) => void;
  languageUX: Language;
};

type ViewType = "fb-loading" | "login" | "register" | "verification";

type Language = "th" | "en";

type MenuTab = "home" | "features";

type HomeListItem = {
  id: number;
  icon: string;
  header: string;
  description: string;
};

type FeatureHandle = {
  featureItems: Record<number, HTMLDivElement | null>;
};

const IMAGES = {
  bookingManagementIcon: "/static/images/mehey_landing/icon-booking-management.svg",
  selfServiceBookingIcon: "/static/images/mehey_landing/icon-self-service-booking.svg",
  aiBookingIcon: "/static/images/mehey_landing/icon-booking-with-ai.svg",
  insightIcon: "/static/images/mehey_landing/icon-insights-and-analytics.svg",
};

const LIST_ITEMS: HomeListItem[] = [
  {
    id: 1,
    icon: IMAGES.bookingManagementIcon,
    header: "Booking Management",
    description: "บริหารจัดการนัดหมายอย่างมีประสิทธิภาพ",
  },
  {
    id: 2,
    icon: IMAGES.selfServiceBookingIcon,
    header: "Self-Service Booking",
    description: "จอง เลื่อน หรือยกเลิกนัดได้ด้วยตัวเองตลอดเวลา",
  },
  {
    id: 3,
    icon: IMAGES.aiBookingIcon,
    header: "Booking with AI",
    description: "AI ช่วยแนะนำบริการและจัดการนัดหมายอัตโนมัติ",
  },
  {
    id: 4,
    icon: IMAGES.insightIcon,
    header: "Insights & Analytics",
    description: "สรุปข้อมูลเพื่อใช้วิเคราะห์และพัฒนาธุรกิจ",
  },
];

export const BREAKPOINT = {
  HANDHELD: 360,
  MOBILE: 768,
  DESKTOP_SM: 1024,
  DESKTOP_SMD: 1228,
  DESKTOP_MD: 1280,
  DESKTOP_LG: 1440,
};

/*================================ MAIN ==============================*/

const MeHeyLanding = (props: MeHeyLandingProps) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [activeLandingTab, setActiveLandingTab] = useState<MenuTab>("home");
  const [openAddLineMod, setOpenAddLineMod] = useState<boolean>(false);
  const [openDownloadMod, setOpenDownloadMod] = useState<boolean>(false);
  const [openLoginMod, setOpenLoginMod] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ViewType>("login");
  const [email, setEmail] = useState<string>("");
  const [disableSnap, setDisableSnap] = useState<boolean>(false);
  const [isSelectedFeature, setIsSelectedFeature] = useState<boolean>(false);

  const homeRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef<any>(false);
  const featureItemsRef = useRef<FeatureHandle>(null);
  const mobileContentRef = useRef<HTMLDivElement>(null);
  const prevTab = useRef<string | null>(null);

  const intl = useIntl();

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVh();

    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);

    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, []);

  useEffect(() => {
    const header = document.querySelector(".mehey-header");
    if (!header) return;

    const update = () => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${header.getBoundingClientRect().height}px`,
      );
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (isScrolling.current) return;

      const delta = event.deltaY;

      // Scroll down
      if (delta > 0) {
        if (activeLandingTab === "home") {
          handleChangeLandingTab("features");
        }
      }

      // Scroll up
      else {
        if (activeLandingTab === "features") {
          const scrollOnTop = featuresRef.current?.scrollTop ?? 0;

          if (scrollOnTop === 0) {
            handleChangeLandingTab("home");
          }
        }
      }

      // Prevent overscrolling spam
      isScrolling.current = true;
      setTimeout(() => (isScrolling.current = false), 700);
    },
    [activeLandingTab],
  );

  useEffect(() => {
    const isEnteredFeaturePage = prevTab.current !== "features" && activeLandingTab === "features";

    if (isEnteredFeaturePage && !isMobile && !isSelectedFeature) {
      featuresRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }

    prevTab.current = activeLandingTab;
  }, [activeLandingTab, isMobile, isSelectedFeature]);

  useEffect(() => {
    if (disableSnap) {
      document.body.classList.add("no-snap");
    } else {
      document.body.classList.remove("no-snap");
    }
  }, [disableSnap]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [activeLandingTab]);

  // --- Start with Thai Lang
  useEffect(() => {
    props.onChangeSelectedLanguage?.(null, { value: "th" });
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const sections = [
      { ref: homeRef, tab: "home" },
      { ref: featuresRef, tab: "features" },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = sections.find((s) => s.ref.current === entry.target);
            if (section && section.tab !== activeLandingTab) {
              setActiveLandingTab(section.tab as MenuTab);
            }
          }
        });
      },
      { threshold: 0.6 },
    );

    sections.forEach((s) => s.ref.current && observer.observe(s.ref.current));
    return () => observer.disconnect();
  }, [activeLandingTab]);

  useEffect(() => {
    if (!isMobile) return;

    const container = mobileContentRef.current;

    if (!container) return;

    const handleScroll = () => {
      const homePos = homeRef.current?.getBoundingClientRect().top ?? 0;
      const featurePos = featuresRef.current?.getBoundingClientRect().top ?? 0;

      if (homePos <= 100 && homePos > -200) {
        if (activeLandingTab !== "home") setActiveLandingTab("home");
      }

      if (featurePos <= 120) {
        if (activeLandingTab !== "features") setActiveLandingTab("features");
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMobile, activeLandingTab]);

  const handleChangeLandingTab = (tab: MenuTab) => {
    setActiveLandingTab(tab);

    if (isMobile) {
      const container = mobileContentRef.current;

      if (!container) return;

      if (isSelectedFeature && tab === "features") {
        return;
      }

      const extraSpacing = handheldCheck() ? 100 : 80;

      if (tab === "home" && homeRef.current) {
        container.scrollTo({
          top: homeRef.current.offsetTop - extraSpacing,
          behavior: "smooth",
        });
      }

      if (tab === "features" && featuresRef.current) {
        container.scrollTo({
          top: featuresRef.current.offsetTop - extraSpacing,
          behavior: "smooth",
        });
      }

      return;
    }

    // --- Desktop
    if (tab === "home" && homeRef.current) {
      homeRef.current.scrollIntoView({ behavior: "smooth" });
    }

    if (tab === "features" && featuresRef.current) {
      featuresRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleChangeLanguage = (language: Language) => {
    props.onChangeSelectedLanguage?.(null, { value: language } as DropdownProps);
  };

  const handleOpenAddLineMod = () => setOpenAddLineMod(true);
  const handleCloseAddLineMod = () => setOpenAddLineMod(false);

  const handleOpenDownloadMod = () => setOpenDownloadMod(true);
  const handleCloseDownloadMod = () => setOpenDownloadMod(false);

  const handleOpenLoginMod = () => setOpenLoginMod(true);
  const handleCloseLoginMod = () => setOpenLoginMod(false);

  const handleRegisterSuccess = (inputEmail: string) => {
    setEmail(inputEmail);

    setCurrentView("verification");
  };

  const handleLoginLoading = (isLoading: boolean) => {
    setCurrentView(isLoading ? "fb-loading" : "login");
  };

  const handheldCheck = () => window.innerWidth <= BREAKPOINT.HANDHELD;
  const mobileCheck = () =>
    window.innerWidth > BREAKPOINT.HANDHELD && window.innerWidth <= BREAKPOINT.MOBILE;

  const getHeaderHeight = () => {
    const headerEl = document.querySelector(".mehey-header") as HTMLElement;
    return headerEl?.getBoundingClientRect().height ?? 0;
  };

  const getExtraScrollSpacing = () => {
    if (handheldCheck()) return 40;
    // if (mobileCheck()) return 35; // --- Mobile ไม่ยังไม่ต้องเพิ่ม Space
    return 0;
  };

  const calculateScrollOffset = ({
    el,
    container,
  }: {
    el: HTMLElement;
    container: HTMLElement;
  }) => {
    const headerHeight = getHeaderHeight();

    const elReact = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const extraSpacing = getExtraScrollSpacing();

    return elReact.top + container.scrollTop - containerRect.top - headerHeight + extraSpacing;
  };

  const scrollToElementInsideContainer = (container: HTMLElement, offset: number) => {
    return new Promise<void>((resolve) => {
      container.scrollTo({
        top: offset,
        behavior: "smooth",
      });

      setTimeout(resolve, 300);
    });
  };

  const handleSelectFeature = async (id: number) => {
    setIsSelectedFeature(true);
    setDisableSnap(true);
    handleChangeLandingTab("features");

    await new Promise((res) => setTimeout(res, 50));

    const el = featureItemsRef.current?.featureItems?.[id];
    const container =
      mobileCheck() || handheldCheck() ? mobileContentRef.current : featuresRef.current;

    if (!el || !container) return;

    const offset = calculateScrollOffset({ el, container });

    await scrollToElementInsideContainer(container, offset);

    setDisableSnap(false);

    setTimeout(() => setIsSelectedFeature(false), 300);
  };

  if (currentView === "fb-loading") {
    return <LoadingScreen />;
  }

  return (
    <div className="main-landing" style={{ overflow: "auto", height: "100vh" }}>
      {/* --- Add Line --- */}
      <MeHeyLandingAddLineMod open={openAddLineMod} onClose={handleCloseAddLineMod} />

      {/* --- Download --- */}
      <MeHeyLandingDownloadMod open={openDownloadMod} onClose={handleCloseDownloadMod} />

      {/* --- Login --- */}
      <MeHeyLandingLoginMod
        open={openLoginMod}
        onClose={handleCloseLoginMod}
        currentView={currentView}
        // login
        AppInitializeSequence={props.AppInitializeSequence}
        setProp={props.setProp}
        controller={props.controller}
        facebookLogin={props.facebookLogin}
        loadingLogin={props.loadingLogin}
        // onEmailRegister={handleEmailRegister}
        // onEmailSuccess={handleEmailSuccess}
        onLoginLoading={handleLoginLoading}
        onLoginSubmit={props.onLoginSubmit}
        onLoginSuccess={props.onLoginSuccess}
        // register
        email={email}
        onRegisterSuccess={handleRegisterSuccess}
      />

      <MyIshealthCloudLanding
        onNavigate={(path) => {
          switch (path) {
            case "/login":
              return handleOpenLoginMod();
            case "/contact":
            case "/line":
              return handleOpenAddLineMod();
            case "/download":
              return handleOpenDownloadMod();
          }
        }}
        lang={props.languageUX}
        onLangChange={(lang) => handleChangeLanguage(lang.toLowerCase() as Language)}
      />
    </div>
  );
};

MeHeyLanding.displayName = "Landing";

export default memo(MeHeyLanding);
